import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FilterChangeParams, FilterConfig, getDefaultFilters } from '@/components/Filter';
import { PaginationConfig } from '@/components/DataTable';
import { syncParamsToUrl, initializeFiltersFromUrl, initializePaginationFromUrl } from '@/utils/urlParams';
import { usePermissions } from '@/hooks/usePermissions';
import { SupportedChainId, SupportedChains } from '@packet/constants/AppSupportChains';
import { useChainStore } from '@/stores/chain';
import { useMultipleTokenInfo } from '@/hooks/useMultipleTokenInfo';
import AppTokenWhitelist from '@packet/constants/AppTokenWhitelist';

export interface UseFilterAndPaginationOptions {
  /** 筛选配置 */
  filterConfigs: FilterConfig[];
  /** 默认分页大小 */
  defaultPageSize?: number;
  /** 数据加载函数 */
  onDataLoad?: (filters: FilterChangeParams, pagination: { current: number; pageSize: number }) => Promise<void>;
  /** 是否自动加载数据 */
  autoLoad?: boolean;
}

export interface UseFilterAndPaginationReturn {
  /** 当前筛选条件 */
  filters: FilterChangeParams;
  /** 当前分页配置 */
  pagination: PaginationConfig;
  /** 加载状态 */
  loading: boolean;
  /** 处理筛选条件变化 */
  handleFilterChange: (newFilters: FilterChangeParams) => void;
  /** 处理分页变化 */
  handlePaginationChange: (page: number, pageSize: number) => void;
  /** 手动触发数据加载 */
  loadData: (filterParams?: FilterChangeParams, paginationParams?: { current: number; pageSize: number }) => Promise<void>;
  /** 设置分页总数 */
  setPaginationTotal: (total: number) => void;
  /** 设置加载状态 */
  setLoading: (loading: boolean) => void;
  /** 动态筛选配置（包含权限处理） */
  dynamicFilterConfigs: FilterConfig[];
  /** Token 信息加载状态 */
  tokenInfoLoading: boolean;
}

/**
 * 通用的筛选和分页hook
 * 解决了作用域陷阱问题，统一了筛选和分页逻辑
 */
export function useFilterAndPagination({ filterConfigs, defaultPageSize = 10, onDataLoad, autoLoad = true }: UseFilterAndPaginationOptions): UseFilterAndPaginationReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { setChainId } = useChainStore();
  const permission = usePermissions();

  // 初始化筛选条件，合并默认值和URL参数
  const [filters, setFilters] = useState<FilterChangeParams>(() => initializeFiltersFromUrl(searchParams, getDefaultFilters(filterConfigs)));

  // 获取当前链下的所有 assetIds
  const currentChainAssetIds = useMemo(() => {
    if (!filters.chain_id) return [];

    const assetIds = permission.asset.filter((p) => p.chainId === Number(filters.chain_id)).map((p) => p.assetId);
    return assetIds;
  }, [filters.chain_id]);

  // 使用 useMultipleTokenInfo 获取所有 token 信息
  const { tokenInfoMap, allLoading: tokenInfoLoading } = useMultipleTokenInfo(currentChainAssetIds, filters.chain_id as SupportedChainId);

  // 动态筛选配置（处理权限相关的选项）
  const dynamicFilterConfigs = useMemo(() => {
    if (!Object.keys(tokenInfoMap).length) return filterConfigs;
    if (!filters.chain_id) return filterConfigs;

    return filterConfigs.map((config) => {
      // 处理token筛选
      if (config.key === 'asset') {
        const options = currentChainAssetIds.map((assetID) => {
          const tokenInfo = tokenInfoMap[assetID];
          return {
            value: assetID,
            label: tokenInfo ? `${tokenInfo.symbol}` : assetID,
            info: tokenInfo,
          };
        });
        const addressIndexs = Object.fromEntries(AppTokenWhitelist.map((item, index) => [item.address, index]));
        options.sort((a, b) => {
          const aIndex = addressIndexs[a.info?.address] ?? Number.MAX_SAFE_INTEGER;
          const bIndex = addressIndexs[b.info?.address] ?? Number.MAX_SAFE_INTEGER;
          if (aIndex !== bIndex) return aIndex - bIndex;
          if (a.info.symbol < b.info.symbol) return -1;
          if (a.info.symbol > b.info.symbol) return 1;
          return 0;
        });
        return {
          ...config,
          options,
        };
      }
      return config;
    });
  }, [filters.chain_id, filterConfigs, currentChainAssetIds, tokenInfoMap]);

  // 分页变化处理函数
  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      const newPagination = { current: page, pageSize };
      setPagination((prev) => ({ ...prev, ...newPagination }));

      // 同步到URL - 使用最新的filters状态
      setFilters((currentFilters) => {
        syncParamsToUrl({ filters: currentFilters, pagination: newPagination }, (path) => navigate(path, { replace: true }), location.pathname);
        return currentFilters;
      });

      // 触发数据加载
      if (onDataLoad) {
        // 使用最新的filters状态
        setFilters((currentFilters) => {
          onDataLoad(currentFilters, newPagination);
          return currentFilters;
        });
      }
    },
    [navigate, location.pathname, onDataLoad],
  );

  // 初始化分页配置
  const [pagination, setPagination] = useState<PaginationConfig>(() => {
    const initialPagination = initializePaginationFromUrl(searchParams, {
      current: 1,
      pageSize: defaultPageSize,
      total: 0,
      onChange: handlePaginationChange,
    });

    // 确保onChange函数是最新的
    return {
      ...initialPagination,
      onChange: handlePaginationChange,
    };
  });

  // 更新分页配置的onChange函数，确保使用最新的函数引用
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      onChange: handlePaginationChange,
    }));
  }, [handlePaginationChange]);

  // 处理筛选条件变化
  const handleFilterChange = useCallback(
    (newFilters: FilterChangeParams) => {
      if (newFilters.chain_id) {
        const chain = SupportedChains.find((chain) => chain.id === newFilters.chain_id);
        if (chain) {
          setChainId(chain.id);
        }
      }
      setFilters(newFilters);

      // 重置分页到第一页
      const newPagination = { current: 1, pageSize: pagination.pageSize };
      setPagination((prev) => ({ ...prev, current: 1, total: 0 }));

      // 同步到URL
      syncParamsToUrl({ filters: newFilters, pagination: newPagination }, (path) => navigate(path, { replace: true }), location.pathname);

      // 触发数据加载
      if (onDataLoad) {
        onDataLoad(newFilters, newPagination);
      }
    },
    [navigate, location.pathname, pagination.pageSize, onDataLoad],
  );

  // 手动触发数据加载
  const loadData = useCallback(
    async (filterParams?: FilterChangeParams, paginationParams?: { current: number; pageSize: number }) => {
      if (onDataLoad) {
        const currentFilters = filterParams || filters;
        const currentPagination = paginationParams || { current: pagination.current, pageSize: pagination.pageSize };
        await onDataLoad(currentFilters, currentPagination);
      }
    },
    [onDataLoad, filters, pagination.current, pagination.pageSize],
  );

  // 设置分页总数
  const setPaginationTotal = useCallback((total: number) => {
    setPagination((prev) => ({ ...prev, total }));
  }, []);

  // 初始化数据加载
  useEffect(() => {
    if (autoLoad && onDataLoad) {
      loadData(filters);
    }
  }, []);

  return {
    filters,
    pagination,
    loading,
    handleFilterChange,
    handlePaginationChange,
    loadData,
    setPaginationTotal,
    setLoading,
    dynamicFilterConfigs,
    tokenInfoLoading,
  };
}
